import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { KeyboardDatePicker } from '@material-ui/pickers';

const US_DATE_FORMAT = 'MM/DD/YYYY';
const ISO_DATE = 'YYYY-MM-DD';
const INVALID_DATE_MESSAGE = 'Enter a date as MM/DD/YYYY.';

// Half-typed text never reaches the form, so a screen diffing against a baseline is blind to it.
const fieldsHoldingUnparsedText = new Set();

export const hasUnparsedDateText = () => fieldsHoldingUnparsedText.size > 0;

/**
 * Date input that always reads and writes US format, whatever the browser is set to.
 *
 * The native `<input type="date">` this replaces takes its format from the
 * browser's language, so a Spanish browser shows `DD/MM/YYYY` — placeholder and
 * filled-in value alike — and no attribute can override that. This one shows
 * `MM/DD/YYYY` and speaks ISO to its caller, so the surrounding form state, the
 * save path and the API contract all stay exactly as they were.
 */
function UsDateField({ name, value, onChange, ...other }) {
  const inputRef = useRef(null);
  const fieldKey = useRef({});

  useEffect(() => () => fieldsHoldingUnparsedText.delete(fieldKey.current), []);

  // Only the calendar part is read, so a value that arrives as a full timestamp
  // still shows the day it names: reparsing '1990-05-03T00:00:00.000Z' in the
  // time zone the app runs in would land on the 2nd.
  const selected = value ? moment(String(value).slice(0, 10), ISO_DATE) : null;

  const handleChange = (date, typed) => {
    const isValid = Boolean(date && date.isValid());

    // What the browser sees in this input is masked text, so a half-typed
    // `05/__/____` — or a well-formed but impossible `13/45/2020` — looks like a
    // filled-in required field while the form below holds nothing. Reporting the
    // mismatch ourselves is what still blocks the submit, the way the native
    // date input used to on its own.
    const isUnparsed = Boolean(typed && !isValid);
    if (inputRef.current) {
      inputRef.current.setCustomValidity(isUnparsed ? INVALID_DATE_MESSAGE : '');
    }

    if (isUnparsed) fieldsHoldingUnparsedText.add(fieldKey.current);
    else fieldsHoldingUnparsedText.delete(fieldKey.current);

    // A synthetic event, so `useForm.handleChange` works unchanged: it only reads
    // `event.target.name` / `.value`, and calls `persist` only when it exists.
    onChange({ target: { name, value: isValid ? date.format(ISO_DATE) : '' } });
  };

  return (
    <KeyboardDatePicker
      name={name}
      inputRef={inputRef}
      value={selected}
      onChange={handleChange}
      format={US_DATE_FORMAT}
      placeholder={US_DATE_FORMAT}
      // `variant` on a picker chooses dialog/inline/static; the outlined look of
      // every field around it is read from `inputVariant` instead.
      inputVariant="outlined"
      autoOk
      InputLabelProps={{ shrink: true }}
      KeyboardButtonProps={{ 'aria-label': 'change date' }}
      {...other}
    />
  );
}

UsDateField.propTypes = {
  name: PropTypes.string.isRequired,
  /** `YYYY-MM-DD`, or empty. */
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

export default UsDateField;
